import { useQuery, useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { invalidateCarbonCaches } from '@/lib/carbonCacheKeys';
import { invalidateAuditCaches } from '@/lib/auditCacheKeys';
import * as Sentry from '@sentry/react';
import { supabase } from '@/integrations/supabase/client';
import { IMPORT_MATCH_EMBED, ACCOUNT_TYPE_EMBED } from '@/integrations/supabase/accountingSelects';
import { useToast } from '@/hooks/useToast';
import {
  authedGet,
  authedPost,
  authedPatch,
  authedDelete,
  VAT_DECLARED_MUTATION_META,
} from '@/lib/api';
import { getAccountDisplayType } from '@/lib/account-category-utils';
import { indexQueryOptions } from '@/hooks/lib/indexQueryOptions';
import { useBulkDelete, type BulkDeleteConfig } from '@/hooks/useBulkDelete';
import { JOURNAL_BATCH_MAX_IDS } from '@releve/shared';
import type {
  AccountFundBalancesResponse,
  JournalBatchDeleteResult,
  AccountRegisterResponse,
  LedgerTransactionIndexRowWire,
  RelatedLedgerTransactionsResponse,
} from '@releve/shared';
import '@/integrations/supabase/rpc-types';
import { trackEvent } from '@/lib/posthog';
import { invalidateProjectMoney } from "@/hooks/useProjectMoneySummary";

export interface LedgerEntry {
  id: string;
  organization_id: string;
  transaction_id: string;
  account_id: string;
  fund_id: string | null;
  debit_amount: number;
  credit_amount: number;
  description: string | null;
  created_at: string;
  tax_rate_id?: string;
  /**
   * The filed VAT return this line was declared on (`file_vat_return` tags it,
   * void untags it). Any tagged line makes the whole document *declared* —
   * frozen lines, header-only saves (`declaredSettlementIdOf`, docs/TAX.md
   * "Edit guard"). Optional: the register's slim selects do not carry it.
   */
  vat_settlement_id?: string | null;
  // Joined data
  account?: {
    account_name: string;
    account_code: string;
    /** The account's own currency; NULL = org currency. */
    currency?: string | null;
    /** Set on system accounts (fx_clearing, suspense, …); NULL otherwise. */
    system_account_type?: string | null;
    category?: {
      category_name: string;
    };
    account_type?: {
      type_name: string;
    };
  };
  fund?: {
    fund_code: string;
    fund_name: string;
  };
  tax_rate?: {
    name: string;
    rate: number;
    direction: string;
  };
}

export interface LedgerTransaction {
  id: string;
  organization_id: string;
  transaction_number: string;
  transaction_date: string;
  reference: string | null;
  description: string;
  total_amount: number;
  transaction_type?: 'income' | 'expense' | 'transfer';  // Computed type based on entries
  project_id: string | null;
  business_id: string | null;
  person_id: string | null;
  fund_id: string | null;
  purchase_order_id: string | null;
  invoice_number: string | null;
  ap_invoice_id: string | null;
  ar_invoice_id: string | null;
  created_by: string;
  created_at: string;
  updated_at: string;
  // Related data
  entries?: LedgerEntry[];
  matched_imports?: Array<{ import: { id: string; booking_date: string; description: string | null } }>;
  created_imports?: Array<{ id: string; booking_date: string; description: string | null }>;
  project?: {
    name: string;
  };
  business?: {
    name: string;
  };
  person?: {
    first_name: string;
    last_name: string;
  };
  fund?: {
    fund_code: string;
    fund_name: string;
  };
  ap_invoice?: {
    id: string;
    invoice_number: string;
  };
  ar_invoice?: {
    id: string;
    invoice_number: string;
    // Channel identity: a `source='channel'` AR row is a sales-channel
    // RECEIPT, not a manual invoice, so the grid's Ref cell routes it to the
    // channel's Sales tab instead of the AR editor (transactionGridMapper).
    // `sales_channel_id` is non-null exactly when source='channel' (CHECK
    // constraint on accounts_receivable).
    source?: string | null;
    sales_channel_id?: string | null;
  };
  expense?: {
    id: string;
    expense_number: string;
    // Payee fallbacks: expense postings don't stamp business_id on the ledger
    // transaction, so the register reads the payee off the linked expense —
    // its FK business or the free-text custom_business_name (feed quick-adds).
    custom_business_name?: string | null;
    business?: { name: string } | null;
  }[];
  // Void linkage from the shared `ledger_void_linkage` view, merged in by a
  // second bulk read after the main query — the reversal that voids a journal
  // can sit OUTSIDE the fetched window (a different day, past the limit), so
  // the strikethrough can't be derived from the fetched set alone.
  void_of_transaction_id?: string | null;
  reverses_transaction_number?: string | null;
  reversed_by_transaction_id?: string | null;
  reversed_by_transaction_number?: string | null;
}

/**
 * Bulk-read `ledger_void_linkage` for the fetched transactions and merge it in,
 * so the consumer that renders strikethroughs (the dashboard tile) marks
 * reversed rows exactly like the index grid and the register do. Chunked
 * `.in()` — an unbounded id list in one GET trips Kong's URL cap (the
 * register's 414).
 *
 * NON-FATAL by design: the linkage feeds a purely cosmetic strikethrough, so a
 * transient failure here must never blank the primary transaction list — log
 * to Sentry and return the rows unmerged.
 */
async function attachVoidLinkage(transactions: LedgerTransaction[]): Promise<LedgerTransaction[]> {
  if (transactions.length === 0) return transactions;
  const CHUNK = 100;
  const ids = transactions.map((transaction) => transaction.id);
  const chunks: string[][] = [];
  for (let i = 0; i < ids.length; i += CHUNK) chunks.push(ids.slice(i, i + CHUNK));
  const linkage = new Map<
    string,
    Pick<
      LedgerTransaction,
      | 'void_of_transaction_id'
      | 'reverses_transaction_number'
      | 'reversed_by_transaction_id'
      | 'reversed_by_transaction_number'
    >
  >();
  try {
    const results = await Promise.all(
      chunks.map((chunk) =>
        supabase.from('ledger_void_linkage').select('*').in('transaction_id', chunk),
      ),
    );
    for (const { data, error } of results) {
      if (error) throw error;
      for (const row of data ?? []) {
        if (!row.transaction_id) continue;
        linkage.set(row.transaction_id, {
          void_of_transaction_id: row.void_of_transaction_id,
          reverses_transaction_number: row.reverses_transaction_number,
          reversed_by_transaction_id: row.reversed_by_transaction_id,
          reversed_by_transaction_number: row.reversed_by_transaction_number,
        });
      }
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: { hook: 'useLedgerTransactions', step: 'attachVoidLinkage' },
    });
    return transactions;
  }
  return transactions.map((transaction) => ({
    ...transaction,
    ...linkage.get(transaction.id),
  }));
}

/**
 * Determine transaction type from ledger entries based on account categories
 * In double-entry bookkeeping:
 * - Revenue accounts (normally credit) when credited = Income
 * - Expense accounts (normally debit) when debited = Expense
 * - Other combinations = Transfer (between assets/liabilities)
 */
function determineTransactionType(entries?: LedgerEntry[]): 'income' | 'expense' | 'transfer' {
  if (!entries || entries.length === 0) {
    return 'transfer'; // Default fallback for unknown
  }

  let hasRevenue = false;
  let hasExpense = false;

  for (const entry of entries) {
    // Use the shared category→bucket classifier so this register classifier and
    // the bank feed's Action label (bankFeedGridColumns.typeLabelKeyForProcessedRow)
    // stay in lockstep — one source of truth for the keyword set. See
    // docs/TRANSACTION_CLASSIFICATION.md.
    const displayType = getAccountDisplayType(entry.account?.category?.category_name);

    // Revenue accounts credited = Income
    if (displayType === 'revenue' && entry.credit_amount && entry.credit_amount > 0) {
      hasRevenue = true;
    }

    // Expense accounts debited = Expense
    if (displayType === 'expense' && entry.debit_amount && entry.debit_amount > 0) {
      hasExpense = true;
    }
  }

  // Prioritize revenue over expense if both exist (mixed transaction)
  if (hasRevenue) {
    return 'income';
  }
  if (hasExpense) {
    return 'expense';
  }

  // Everything else is a transfer (asset/liability movements)
  return 'transfer';
}

export interface CreateTransactionData {
  transaction_date: string;
  reference?: string;
  description: string;
  total_amount: number;
  project_id?: string;
  business_id?: string;
  fund_id?: string;
  purchase_order_id?: string;
  invoice_number?: string;
  entries: {
    account_id: string;
    fund_id?: string;
    debit_amount?: number;
    credit_amount?: number;
    description?: string;
  }[];
}

/**
 * @deprecated LEGACY fat-list read (direct PostgREST select with embeds) —
 * retire it when the Hono backend work for ledger-transaction READS lands.
 * The org-wide list and the project/business/person financial tabs already
 * moved to `useLedgerTransactionIndex` (the slim `ledger_transaction_index`
 * view via the API), which also replaced this hook's
 * projectId/businessId/personId/fundId options; the account register moved
 * to `useAccountTransactionsWithBalance`. Do NOT add new consumers.
 * NOTE for the Hono migration: `includeVoidLinkage` bolts a second read
 * (`ledger_void_linkage`, attachVoidLinkage above) onto this hook for the
 * dashboard tile's strikethrough — the replacement endpoint must carry the
 * void linkage server-side (the index view and register RPC already do).
 *
 * Remaining call sites and their intended migration (audited 2026-07-21):
 *
 * - `MoneyLens.tsx` — BARE call, an UNBOUNDED org-wide fetch. ⚠ The
 *   priority: PostgREST silently caps at 1,000 rows, so any number it
 *   derives goes wrong once an org grows. Move to a SECURITY INVOKER
 *   reporting RPC per docs/DASHBOARD.md (never sum unbounded tables
 *   client-side).
 * - `DashboardTab.tsx` (`limit: 50`) — bounded recent list; move to the
 *   index-backed grid mapper (see RecentTransactionsTable) or an RPC if
 *   it only needs aggregates.
 * - `RecentTransactionsTable.tsx` (range + `limit: 15`) — already renders
 *   through transactions-grid/; only this fetch is legacy. Replace with a
 *   bounded Hono read.
 * - `AccountFinancialTab.tsx` / `AccountActivityTab.tsx` (`accountId`) —
 *   account-scoped; fold into `get_account_transactions_with_balance` (the
 *   register RPC already returns account-scoped rows) or a purpose-built
 *   RPC for their aggregates.
 *
 * Endgame: once those move, delete this hook and the fat
 * `LedgerTransaction` interface (its remaining references are comments,
 * the CAMT import mapper's own shapes, and useEntityLedgerTransactions —
 * which retires with the entity-tab metrics migration). Route the new
 * reads through the Hono API per docs/BACKEND_API.md ("Read endpoints").
 *
 * ⚠ Known defect, deliberately NOT fixed here: the accountId path's
 * `.in('id', transactionIds)` below is unbounded — the same oversized-URL /
 * silent-1,000-row-truncation pair the register route fixed with
 * `fetchInChunks` (apps/api/src/lib/postgrestChunks.ts). Chunking a hook
 * slated for deletion is wasted work; the migrations above are the fix.
 */
export const useLedgerTransactions = (organizationId?: string, options?: {
  accountId?: string;
  /** Inclusive lower bound on transaction_date (YYYY-MM-DD). */
  startDate?: string;
  /** Inclusive upper bound on transaction_date (YYYY-MM-DD). */
  endDate?: string;
  limit?: number;
  /**
   * Opt-in: merge `ledger_void_linkage` into the rows (attachVoidLinkage) so
   * void strikethroughs render. Only the dashboard tile reads the linkage —
   * the account tabs never do, so they must not pay the extra round trips.
   */
  includeVoidLinkage?: boolean;
}) => {
  return useQuery({
    queryKey: ['ledgerTransactions', organizationId, options],
    queryFn: async () => {
      if (!organizationId) return [];

      // When filtering by accountId, we need a two-phase approach:
      // 1. Find transaction IDs that have at least one entry with this account
      // 2. Fetch those transactions with ALL their entries (for full double-entry view)
      if (options?.accountId) {
        // Phase 1: Get transaction IDs that have entries with this account
        const { data: matchingEntries, error: entriesError } = await supabase
          .from('ledger_entries')
          .select('transaction_id')
          .eq('account_id', options.accountId);

        if (entriesError) throw entriesError;

        const transactionIds = [...new Set(matchingEntries?.map(e => e.transaction_id) || [])];

        if (transactionIds.length === 0) return [];

        // Phase 2: Fetch transactions with lightweight entries for list view
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- typed client over-narrows this dynamically-filtered nested select; result is mapped to LedgerTransaction below
        let query = (supabase as any)
          .from('ledger_transactions')
          .select(`
            *,
            project:projects(name),
            business:businesses(name),
            person:people(first_name, last_name),
            fund:funds(fund_code, fund_name),
            ap_invoice:accounts_payable!ledger_transactions_ap_invoice_id_fkey(id, invoice_number),
            ar_invoice:accounts_receivable!ledger_transactions_ar_invoice_id_fkey(id, invoice_number, source, sales_channel_id),
            expense:expenses!expenses_ledger_transaction_id_fkey(id, expense_number, custom_business_name, business:businesses(name)),
            ${IMPORT_MATCH_EMBED},
            entries:ledger_entries(
              id,
              account_id,
              debit_amount,
              credit_amount,
              account:chart_of_accounts(
                account_name,
                account_code:account_number,
                currency,
                system_account_type,
                category:financial_account_categories(category_name),
                ${ACCOUNT_TYPE_EMBED}
              )
            )
          `)
          .eq('organization_id', organizationId)
          .in('id', transactionIds);

        // Apply other filters
        if (options?.startDate) {
          query = query.gte('transaction_date', options.startDate);
        }
        if (options?.endDate) {
          // `transaction_date` is a plain `date`, so an inclusive upper bound
          // needs no day-after widening.
          query = query.lte('transaction_date', options.endDate);
        }
        if (options?.limit) {
          query = query.limit(options.limit);
        }

        // `transaction_date` is a plain `date`: a whole day of journals ties on
        // it, so entry order (`created_at`, then `id`) breaks the tie and the
        // list stops reshuffling between fetches.
        const { data, error } = await query
          .order('transaction_date', { ascending: false })
          .order('created_at', { ascending: false })
          .order('id', { ascending: false });

        if (error) throw error;

        const transactionsWithType = (data || []).map((transaction: LedgerTransaction) => ({
          ...transaction,
          transaction_type: determineTransactionType(transaction.entries)
        }));

        const typed = transactionsWithType as LedgerTransaction[];
        return options?.includeVoidLinkage ? attachVoidLinkage(typed) : typed;
      }

      // Standard query without accountId filter
      // Entries use a lightweight select: only fields needed for list view
      // (AccountFlowDisplay, determineTransactionType, account filter options).
      // Full entry details are lazy-loaded via useLedgerTransactionEntries on row expand.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- typed client over-narrows this dynamically-filtered nested select; result is mapped to LedgerTransaction below
      let query = (supabase as any)
        .from('ledger_transactions')
        .select(`
          *,
          project:projects(name),
          business:businesses(name),
          person:people(first_name, last_name),
          fund:funds(fund_code, fund_name),
          ap_invoice:accounts_payable!ledger_transactions_ap_invoice_id_fkey(id, invoice_number),
          ar_invoice:accounts_receivable!ledger_transactions_ar_invoice_id_fkey(id, invoice_number, source, sales_channel_id),
          expense:expenses!expenses_ledger_transaction_id_fkey(id, expense_number, custom_business_name, business:businesses(name)),
          ${IMPORT_MATCH_EMBED},
          entries:ledger_entries(
            id,
            account_id,
            debit_amount,
            credit_amount,
            account:chart_of_accounts(
              account_name,
              account_code:account_number,
              currency,
              system_account_type,
              category:financial_account_categories(category_name),
              ${ACCOUNT_TYPE_EMBED}
            )
          )
        `)
        .eq('organization_id', organizationId);

      if (options?.startDate) {
        query = query.gte('transaction_date', options.startDate);
      }

      if (options?.endDate) {
        // `transaction_date` is a plain `date`, so an inclusive upper bound
        // needs no day-after widening.
        query = query.lte('transaction_date', options.endDate);
      }

      if (options?.limit) {
        query = query.limit(options.limit);
      }

      // Same tiebreak as the accountId branch above: a plain `date` ties across
      // a whole day, so entry order (`created_at`, then `id`) settles it.
      const { data, error } = await query
        .order('transaction_date', { ascending: false })
        .order('created_at', { ascending: false })
        .order('id', { ascending: false });

      if (error) throw error;

      // Add computed transaction type to each transaction
      const transactionsWithType = (data || []).map((transaction: LedgerTransaction) => ({
        ...transaction,
        transaction_type: determineTransactionType(transaction.entries)
      }));

      const typed = transactionsWithType as LedgerTransaction[];
      return options?.includeVoidLinkage ? attachVoidLinkage(typed) : typed;
    },
    enabled: !!organizationId,
    staleTime: 2 * 60 * 1000, // 2 minutes — avoids refetch on remount/focus
  });
};

/** One row of the slim transaction index (the `ledger_transaction_index` DB view). */
export type LedgerTransactionIndexRow = LedgerTransactionIndexRowWire;

/**
 * The COMPLETE slim transaction index for an org — the API pages the
 * `ledger_transaction_index` view past PostgREST's 1,000-row cap, so it is
 * never silently truncated. Powers the transactions grid's client-side
 * search/sort/filter; heavy detail (full entries with funds/tax rates,
 * related transactions) is fetched lazily per row via
 * `useLedgerTransactionEntries` / `useRelatedLedgerTransactions`.
 *
 * `transaction_type` arrives server-derived from the entries' account
 * categories (the view's KEEP IN SYNC twin of `determineTransactionType`
 * below — docs/TRANSACTION_CLASSIFICATION.md).
 *
 * Entity options filter on the view's linkage ARRAYS (header FK ∪ linked
 * expense ∪ AP invoice ∪ AR invoice), matching what
 * useEntityLedgerTransactions unions — not the sparse header columns.
 *
 * Liveness: the key sits under the `['ledgerTransactions', orgId]` prefix,
 * so the journal mutations in this file and every cross-domain flow that
 * already invalidates that prefix (expenses, AP/AR payments, VAT
 * settlement, apply-to-invoice, clearing/import links, currency transfers)
 * refresh it with no mutator changes. `staleTime` only suppresses redundant
 * mount/focus refetches between mutations.
 */
export const useLedgerTransactionIndex = (
  organizationId?: string,
  options?: { projectId?: string; businessId?: string; personId?: string; fundId?: string },
) => {
  // Normalized 4-key shape — a stable key object prevents cache
  // fragmentation between surfaces that omit different options. `fundId`
  // matches the journal's HEADER fund only (the fund detail Transactions tab).
  const normalized = {
    projectId: options?.projectId ?? null,
    businessId: options?.businessId ?? null,
    personId: options?.personId ?? null,
    fundId: options?.fundId ?? null,
  };
  // Key shape, enabled gate, {items} unwrap, and the same-org placeholder
  // guard (no cross-org flash) all live in indexQueryOptions.
  return useQuery(
    indexQueryOptions<LedgerTransactionIndexRowWire>({
      entity: 'ledgerTransactions',
      organizationId,
      path: `/api/v1/ledger-transactions/orgs/${organizationId}/journal-entries/index`,
      params: normalized,
    }),
  );
};

/**
 * The exact wire body `POST /api/v1/ledger-transactions/orgs/:orgId/journal-entries`
 * receives (`createJournalTransactionSchema` in @releve/shared). ONE function
 * feeds both the create mutation below and the recurring-template payload
 * (`buildRecurringPayload('journal', body)` strips `transaction_date`), so a
 * key added here reaches the scheduler's replayed payload the moment it
 * lands. Pure — the `|| null` coercions exist because the modal spreads raw
 * form state where unselected fields are `''`.
 */
export const buildJournalCreateBody = (data: CreateTransactionData) => ({
  transaction_date: data.transaction_date,
  description: data.description,
  total_amount: data.total_amount,
  // The modal spreads raw form state where unselected fields are `''`;
  // coerce empty → null (|| not ??) so the uuid schema fields accept them.
  reference: data.reference || null,
  project_id: data.project_id || null,
  business_id: data.business_id || null,
  fund_id: data.fund_id || null,
  purchase_order_id: data.purchase_order_id || null,
  invoice_number: data.invoice_number || null,
  entries: data.entries.map((entry) => ({
    account_id: entry.account_id,
    fund_id: entry.fund_id || null,
    debit_amount: entry.debit_amount ?? 0,
    credit_amount: entry.credit_amount ?? 0,
    description: entry.description || null,
  })),
});

export type JournalCreateBody = ReturnType<typeof buildJournalCreateBody>;

/**
 * Every cache a NEW journal entry changes. Shared by
 * `useCreateLedgerTransaction` and the recurring "Run now" mutation
 * (`invalidateGeneratedDocumentCaches`), which posts journals without going
 * through this hook.
 */
export const invalidateJournalCreateCaches = (
  queryClient: ReturnType<typeof useQueryClient>,
  organizationId: string,
) => {
  // Open History panels / the org Audit Log (docs/AUDIT.md §10a).
  invalidateAuditCaches(queryClient, organizationId);
  void queryClient.invalidateQueries({ queryKey: ['ledgerTransactions', organizationId] });
  // Project spend totals (useProjectMoneySummary) read the same journals.
  invalidateProjectMoney(queryClient, organizationId);
  void queryClient.invalidateQueries({ queryKey: ['businesses'] });
  // NL WKR bridge rows are rebuilt/cleaned by the journal RPCs + triggers.
  void queryClient.invalidateQueries({ queryKey: ['nl-wkr', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['projectFinancials'] });
  void queryClient.invalidateQueries({ queryKey: ['accountBalances'] });
  // Account `current_balance` (calculate_account_balances) rides on the chart
  // query — the Other accounts / Bank cards and the chart index grid.
  void queryClient.invalidateQueries({ queryKey: ['chartOfAccounts', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['suspense-account-balance', organizationId] });
  // The per-account register (AccountOverviewTab / bank overview) — prefix
  // match so EVERY account the journal touched refetches; its rows AND the
  // running balance both change on create/update/delete.
  void queryClient.invalidateQueries({ queryKey: ['accountTransactionsWithBalance', organizationId] });
  // Related-transactions panels (keyed per transaction) may gain/lose links.
  void queryClient.invalidateQueries({ queryKey: ['relatedLedgerTransactions'] });
  // The expense index carries transaction-sourced fields (reference,
  // touches_bank, import links) — keep it live past its staleTime.
  void queryClient.invalidateQueries({ queryKey: ['expenses', organizationId] });
  // The AP bill index has the same dependency (ledger_reference + the
  // bank-feed badge flags are transaction-sourced).
  void queryClient.invalidateQueries({ queryKey: ['accountsPayable', organizationId] });
  // The AR invoice index too — ledger_reference, fund_id, ar_account_id
  // and ar_account_currency all come from the posting journal.
  void queryClient.invalidateQueries({ queryKey: ['accountsReceivable', organizationId] });
};

export const useCreateLedgerTransaction = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ organizationId, data }: { organizationId: string; data: CreateTransactionData }) => {
      // Fast client-side balance feedback; create_journal_transaction re-validates
      // authoritatively server-side.
      const totalDebits = data.entries.reduce((sum, entry) => sum + (entry.debit_amount || 0), 0);
      const totalCredits = data.entries.reduce((sum, entry) => sum + (entry.credit_amount || 0), 0);

      if (Math.abs(totalDebits - totalCredits) > 0.01) {
        throw new Error('Transaction must balance: total debits must equal total credits');
      }

      if (Math.abs(totalDebits - data.total_amount) > 0.01) {
        throw new Error('Total amount must equal sum of debits (or credits)');
      }

      // HIGH-1 (SCRUM-181): route the ledger write through the Hono backend
      // (create_journal_transaction RPC) instead of writing to ledger_transactions
      // / ledger_entries directly. created_by is stamped server-side from
      // auth.uid(). entry_ids come back in the order sent (form order).
      const result = await authedPost<{
        transaction_id: string;
        transaction_number: string;
        entry_ids: string[];
      }>(`/api/v1/ledger-transactions/orgs/${organizationId}/journal-entries`, buildJournalCreateBody(data));

      return {
        id: result.transaction_id,
        transaction_number: result.transaction_number,
        entry_ids: result.entry_ids,
      };
    },
    onSuccess: (_, { organizationId, data }) => {
      invalidateJournalCreateCaches(queryClient, organizationId);
      trackEvent('journal_created', { entry_count: data.entries.length });

      toast({
        title: "Transaction created",
        description: "The ledger transaction has been created successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error creating transaction",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

/**
 * Cache fan-out shared by the full and the header-only journal updates. A key
 * dropped from one and not the other shows up as a stale row after the other
 * kind of save.
 */
function invalidateLedgerTransactionUpdateCaches(queryClient: QueryClient, organizationId: string) {
  // Open History panels / the org Audit Log (docs/AUDIT.md §10a).
  invalidateAuditCaches(queryClient, organizationId);
  void queryClient.invalidateQueries({ queryKey: ['ledgerTransactions', organizationId] });
  // Project spend totals (useProjectMoneySummary) read the same journals.
  invalidateProjectMoney(queryClient, organizationId);
  void queryClient.invalidateQueries({ queryKey: ['businesses'] });
  // NL WKR bridge rows are rebuilt/cleaned by the journal RPCs + triggers.
  void queryClient.invalidateQueries({ queryKey: ['nl-wkr', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['projectFinancials'] });
  void queryClient.invalidateQueries({ queryKey: ['accountBalances'] });
  // Account `current_balance` (calculate_account_balances) rides on the chart
  // query — the Other accounts / Bank cards and the chart index grid.
  void queryClient.invalidateQueries({ queryKey: ['chartOfAccounts', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['suspense-account-balance', organizationId] });
  // The per-account register (AccountOverviewTab / bank overview) — prefix
  // match so EVERY account the journal touched refetches; its rows AND the
  // running balance both change on create/update/delete.
  void queryClient.invalidateQueries({ queryKey: ['accountTransactionsWithBalance', organizationId] });
  // The expanded row's lazily-loaded entries (useLedgerTransactionEntries,
  // keyed per transaction id) — an edit rewrites the entries (the header-only
  // path still moves fund_id on them), so an open detail panel must refetch
  // instead of showing the pre-edit lines.
  void queryClient.invalidateQueries({ queryKey: ['ledgerTransactionEntries'] });
  // Related-transactions panels (keyed per transaction) may gain/lose links.
  void queryClient.invalidateQueries({ queryKey: ['relatedLedgerTransactions'] });
  // The expense index carries transaction-sourced fields (reference,
  // touches_bank, import links) — keep it live past its staleTime.
  void queryClient.invalidateQueries({ queryKey: ['expenses', organizationId] });
  // The AP bill index has the same dependency (ledger_reference + the
  // bank-feed badge flags are transaction-sourced).
  void queryClient.invalidateQueries({ queryKey: ['accountsPayable', organizationId] });
  // The AR invoice index too — ledger_reference, fund_id, ar_account_id
  // and ar_account_currency all come from the posting journal.
  void queryClient.invalidateQueries({ queryKey: ['accountsReceivable', organizationId] });
  // The Impact slim index shows the journal's description/date/project
  // on its carbon rows; a delete cascades them away
  // (useSustainabilityActivityIndex contract).
  invalidateCarbonCaches(queryClient, organizationId);
}

export const useUpdateLedgerTransaction = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    meta: VAT_DECLARED_MUTATION_META,
    mutationFn: async ({ id, organizationId, data }: { id: string; organizationId: string; data: CreateTransactionData }) => {
      if (!id) {
        throw new Error('Transaction ID is required for update');
      }

      // SCRUM-181/182 Phase II: route through the Hono backend
      // (update_ledger_transaction_with_entries) instead of a direct rpc.
      return authedPatch<Record<string, unknown> | null>(
        `/api/v1/ledger-transactions/orgs/${organizationId}/journal-entries/${id}`,
        {
          transaction_date: data.transaction_date,
          description: data.description,
          total_amount: data.total_amount,
          reference: data.reference || null,
          project_id: data.project_id || null,
          business_id: data.business_id || null,
          fund_id: data.fund_id || null,
          purchase_order_id: data.purchase_order_id || null,
          invoice_number: data.invoice_number || null,
          entries: data.entries.map((entry) => ({
            account_id: entry.account_id,
            fund_id: entry.fund_id || null,
            debit_amount: entry.debit_amount ?? 0,
            credit_amount: entry.credit_amount ?? 0,
            description: entry.description || null,
          })),
        },
      );
    },
    onSuccess: (_, { organizationId }) => {
      invalidateLedgerTransactionUpdateCaches(queryClient, organizationId);

      toast({
        title: "Transaction updated",
        description: "The ledger transaction has been updated successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error updating transaction",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

/** The header fields a declared journal can still change — never a line. */
export interface UpdateLedgerTransactionHeaderData {
  description: string;
  reference?: string | null;
  project_id?: string | null;
  business_id?: string | null;
  fund_id?: string | null;
  purchase_order_id?: string | null;
  invoice_number?: string | null;
}

/**
 * Header-only save for a **declared** manual journal
 * (`update_ledger_transaction_header`, plan "Declared documents in the UI").
 * `useUpdateLedgerTransaction` deletes and re-posts every entry, which the
 * declared-VAT guard refuses once the period is filed; this route writes the
 * header columns only and takes no entries at all — it never touches
 * `ledger_entries` (not even `fund_id`, which is why the modal freezes the
 * header fund on a declared journal: nothing would carry it down to the
 * lines). The modal picks it when `declaredSettlementIdOf(transaction.entries)`
 * is set.
 *
 * Same cache fan-out as the full update — the header is what the indexes,
 * the register and the Impact rows display; the entries cache is included
 * only for symmetry with the full sibling.
 */
export const useUpdateLedgerTransactionHeader = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    meta: VAT_DECLARED_MUTATION_META,
    mutationFn: async ({
      organizationId,
      transactionId,
      ...header
    }: { organizationId: string; transactionId: string } & UpdateLedgerTransactionHeaderData) => {
      if (!transactionId) {
        throw new Error('Transaction ID is required for update');
      }

      return authedPatch<{ success: boolean }>(
        `/api/v1/ledger-transactions/orgs/${organizationId}/journal-entries/${transactionId}/header`,
        {
          description: header.description,
          reference: header.reference || null,
          project_id: header.project_id || null,
          business_id: header.business_id || null,
          fund_id: header.fund_id || null,
          purchase_order_id: header.purchase_order_id || null,
          invoice_number: header.invoice_number || null,
        },
      );
    },
    onSuccess: (_, { organizationId }) => {
      invalidateLedgerTransactionUpdateCaches(queryClient, organizationId);

      toast({
        title: "Transaction updated",
        description: "The ledger transaction has been updated successfully.",
      });
    },
    onError: (error) => {
      // The declared-VAT refusal's human text IS error.message (no branch).
      toast({
        title: "Error updating transaction",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

/**
 * Every cache a journal delete can move — shared by the single delete
 * (`useDeleteLedgerTransaction`) and the grids' bulk delete
 * (`useBulkDeleteJournals`).
 */
export function invalidateJournalDeleteCaches(queryClient: QueryClient, organizationId: string) {
  invalidateAuditCaches(queryClient, organizationId);
  void queryClient.invalidateQueries({ queryKey: ['ledgerTransactions', organizationId] });
  // Project spend totals (useProjectMoneySummary) read the same journals.
  invalidateProjectMoney(queryClient, organizationId);
  void queryClient.invalidateQueries({ queryKey: ['businesses'] });
  // NL WKR bridge rows are rebuilt/cleaned by the journal RPCs + triggers.
  void queryClient.invalidateQueries({ queryKey: ['nl-wkr', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['projectFinancials'] });
  void queryClient.invalidateQueries({ queryKey: ['accountBalances'] });
  // Account `current_balance` (calculate_account_balances) rides on the chart
  // query — the Other accounts / Bank cards and the chart index grid.
  void queryClient.invalidateQueries({ queryKey: ['chartOfAccounts', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['suspense-account-balance', organizationId] });
  // The per-account register (AccountOverviewTab / bank overview) — prefix
  // match so EVERY account the journal touched refetches; its rows AND the
  // running balance both change on create/update/delete.
  void queryClient.invalidateQueries({ queryKey: ['accountTransactionsWithBalance', organizationId] });
  // Related-transactions panels (keyed per transaction) may gain/lose links.
  void queryClient.invalidateQueries({ queryKey: ['relatedLedgerTransactions'] });
  // The expense index carries transaction-sourced fields (reference,
  // touches_bank, import links) — keep it live past its staleTime.
  void queryClient.invalidateQueries({ queryKey: ['expenses', organizationId] });
  // The AP bill index has the same dependency (ledger_reference + the
  // bank-feed badge flags are transaction-sourced).
  void queryClient.invalidateQueries({ queryKey: ['accountsPayable', organizationId] });
  // The AR invoice index too — ledger_reference, fund_id, ar_account_id
  // and ar_account_currency all come from the posting journal.
  void queryClient.invalidateQueries({ queryKey: ['accountsReceivable', organizationId] });
  // The Impact slim index shows the journal's description/date/project
  // on its carbon rows; a delete cascades them away
  // (useSustainabilityActivityIndex contract).
  invalidateCarbonCaches(queryClient, organizationId);
  // The bank feed: a deleted journal can leave a feed row's clearing state
  // and Import History stale (the account's clearing bridge,
  // ['accountBalances', org, 'clearingSummary'], is covered by the
  // ['accountBalances'] prefix above).
  void queryClient.invalidateQueries({ queryKey: ['accountingImports', organizationId] });
  void queryClient.invalidateQueries({ queryKey: ['import-batches', organizationId] });
}

export const useDeleteLedgerTransaction = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    meta: VAT_DECLARED_MUTATION_META,
    mutationFn: async ({ id, organizationId }: { id: string; organizationId: string }) => {
      // SCRUM-181/182 Phase II: route atomic deletion through the Hono backend
      // (delete_ledger_transaction_with_entries). The route surfaces the RPC's
      // {success:false} envelope (e.g. linked expenses) as a 400, which
      // authedDelete throws.
      return authedDelete<{ success: boolean }>(
        `/api/v1/ledger-transactions/orgs/${organizationId}/journal-entries/${id}`,
      );
    },
    onSuccess: (_, { organizationId }) => {
      invalidateJournalDeleteCaches(queryClient, organizationId);

      toast({
        title: "Transaction deleted",
        description: "The ledger transaction has been deleted successfully.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error deleting transaction",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

// ---------- Bulk delete (the journal grids' bulk actions) ----------

const JOURNAL_BULK_DELETE: BulkDeleteConfig = {
  feature: 'journals',
  sentryTags: { journal_action: 'bulk-delete' },
  route: {
    path: (organizationId) =>
      `/api/v1/ledger-transactions/orgs/${organizationId}/journal-entries/batch-delete`,
    idsKey: 'transaction_ids',
    idField: 'transaction_id',
    maxIds: JOURNAL_BATCH_MAX_IDS,
  },
  invalidate: invalidateJournalDeleteCaches,
  i18n: {
    done: 'ledgerTransactions.bulkDelete.toastDone',
    rowFailed: 'ledgerTransactions.bulkDelete.rowFailed',
    outcomeUnknown: 'ledgerTransactions.bulkDelete.outcomeUnknown',
    vatDeclared: 'ledgerTransactions.bulkDelete.vatDeclared',
  },
};

/**
 * Bulk delete for the journal grids (org Journals + the account register):
 * POST /api/v1/ledger-transactions/orgs/:orgId/journal-entries/batch-delete,
 * which runs the SAME `delete_ledger_transaction_with_entries` RPC as the
 * single DELETE route, one id at a time. No pre-skip — every rule (invoice
 * postings and payments, bank-linked, void pairs, opening balances, declared
 * VAT) is the RPC's; its refusals come back per row with number-free texts,
 * so N rows refused the same way are one toast (`useBulkDelete`).
 */
export function useBulkDeleteJournals() {
  return useBulkDelete<JournalBatchDeleteResult>(JOURNAL_BULK_DELETE);
}

export const useUpdateLedgerEntryAccount = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    meta: VAT_DECLARED_MUTATION_META,
    mutationFn: async ({
      entryId,
      organizationId,
      newAccountId
    }: {
      entryId: string;
      organizationId: string;
      newAccountId: string;
    }) => {
      // HIGH-1 (SCRUM-181): route the ledger_entries write through the Hono
      // backend so it carries the HMAC. The validate_ledger_entry_balance
      // trigger still validates the transaction stays balanced server-side.
      return authedPatch<{ success: boolean }>(
        `/api/v1/ledger-transactions/orgs/${organizationId}/entries/${entryId}/account`,
        { account_id: newAccountId },
      );
    },
    onSuccess: (_, { organizationId }) => {
      invalidateAuditCaches(queryClient, organizationId);
      // Invalidate related queries to refresh the UI
      void queryClient.invalidateQueries({ queryKey: ['ledgerTransactions', organizationId] });
      // Project spend totals (useProjectMoneySummary) read the same journals.
      invalidateProjectMoney(queryClient, organizationId);
      void queryClient.invalidateQueries({ queryKey: ['businesses'] });
      // NL WKR bridge rows are rebuilt/cleaned by the journal RPCs + triggers.
      void queryClient.invalidateQueries({ queryKey: ['nl-wkr', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['ledgerTransactionEntries'] });
      void queryClient.invalidateQueries({ queryKey: ['accountBalances', organizationId] });
      // Account `current_balance` (calculate_account_balances) rides on the chart
      // query — the Other accounts / Bank cards and the chart index grid.
      void queryClient.invalidateQueries({ queryKey: ['chartOfAccounts', organizationId] });
      void queryClient.invalidateQueries({ queryKey: ['suspense-account-balance', organizationId] });
      // Recategorizing moves an entry between accounts — both accounts' per-
      // account registers must refetch (rows + running balance).
      void queryClient.invalidateQueries({ queryKey: ['accountTransactionsWithBalance', organizationId] });
      // The expense index carries transaction-sourced fields (touches_bank
      // shifts when an entry moves on/off a feed account) — keep it live.
      void queryClient.invalidateQueries({ queryKey: ['expenses', organizationId] });
      // The AP bill index's has_unmatched_bank flips on the same account move —
      // and this hook is reachable from INSIDE the bill expansion panel, so a
      // stale badge here is immediately visible on the row above.
      void queryClient.invalidateQueries({ queryKey: ['accountsPayable', organizationId] });
      // Identically for the AR invoice index (ar_account_id /
      // ar_account_currency / has_unmatched_bank) — and this hook is reachable
      // from INSIDE the AR expansion panel too.
      void queryClient.invalidateQueries({ queryKey: ['accountsReceivable', organizationId] });

      toast({
        title: "Account updated",
        description: "The ledger entry account has been updated.",
      });
    },
    onError: (error) => {
      toast({
        title: "Error updating account",
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

export const useAccountBalances = (organizationId?: string, options?: {
  accountIds?: string[];
  fundId?: string;
  asOfDate?: string;
}) => {
  return useQuery({
    queryKey: ['accountBalances', organizationId, options],
    queryFn: async () => {
      if (!organizationId) return [];

      // Server-side aggregation: one row per account+fund pair with raw
      // debit/credit totals. Avoids the 1,000-row PostgREST cap that
      // silently truncated raw ledger_entries downloads on large orgs.
      //
      // Routed through the Hono backend (GET /orgs/:orgId/account-balances)
      // rather than a direct supabase.rpc call — the route runs the same
      // SECURITY INVOKER RPC on the per-request user client, so org RLS is
      // still the authorization boundary. Totals come back RAW (unsigned):
      // the normal-balance fold below stays here, with this consumer, because
      // routes/accounting/funds.ts serves the same RPC through a different fold.
      //
      // An absent/empty account list is OMITTED from the query string, so the
      // route omits `p_account_ids` and the RPC's param DEFAULT applies — the
      // same semantics as the old `accountIds?.length ? ... : undefined`.
      // Never send an empty list: that would filter everything out.
      const query = new URLSearchParams();
      if (options?.accountIds?.length) {
        query.set('accountIds', options.accountIds.join(','));
      }
      if (options?.fundId) query.set('fundId', options.fundId);
      if (options?.asOfDate) query.set('asOfDate', options.asOfDate);
      const queryString = query.toString();

      const { balances } = await authedGet<AccountFundBalancesResponse>(
        `/api/v1/accounts/orgs/${organizationId}/account-balances${queryString ? `?${queryString}` : ''}`,
      );

      // Balances by account and fund, signed by the account's normal balance
      interface BalanceEntry {
        account: {
          account_name: string;
          normal_balance: string;
          category?: {
            category_name: string;
          };
        };
        fund?: {
          fund_code: string;
          fund_name: string;
        };
        debit_total: number;
        credit_total: number;
        balance: number;
      }

      return balances.map((row): BalanceEntry => ({
        account: {
          account_name: row.account_name,
          normal_balance: row.normal_balance,
          category: row.category_name ? { category_name: row.category_name } : undefined,
        },
        fund: row.fund_code ? { fund_code: row.fund_code, fund_name: row.fund_name } : undefined,
        debit_total: row.total_debits,
        credit_total: row.total_credits,
        balance: row.normal_balance === 'debit'
          ? row.total_debits - row.total_credits
          : row.total_credits - row.total_debits,
      }));
    },
    enabled: !!organizationId,
    staleTime: 2 * 60 * 1000, // dashboard summary data; avoid refetch on every mount/focus
  });
};

/**
 * Transaction with running balance as returned by the RPC function
 */
export interface TransactionWithRunningBalance {
  id: string;
  organization_id: string;
  transaction_number: string;
  transaction_date: string;
  reference: string | null;
  description: string;
  total_amount: number;
  transaction_type: 'income' | 'expense' | 'transfer' | null;
  /**
   * The DB tag `transaction_type = 'opening_balance'`, exposed by the RPC
   * under a SEPARATE name on purpose: the hook below OVERWRITES
   * `transaction_type` with the derived income/expense/transfer classifier,
   * so `transaction_type === 'opening_balance'` is never true on a register
   * row. Consumers that need the sparse DB tag must read this flag.
   *
   * Drives the register's Cleared column: an opening-balance journal is
   * cleared by definition (it states the bank's starting position, so it can
   * never carry a feed line). Without this term the register would report one
   * more uncleared row than the clearing bridge on every real account.
   */
  is_opening_balance: boolean;
  project_id: string | null;
  business_id: string | null;
  person_id: string | null;
  fund_id: string | null;
  purchase_order_id: string | null;
  invoice_number: string | null;
  ap_invoice_id: string | null;
  ar_invoice_id: string | null;
  // This transaction's bank-feed import links ON THIS ACCOUNT (the RPC
  // json_aggs them so split-matched transactions surface every allocation, and
  // scopes both LATERALs to the register's account). Shapes mirror the
  // PostgREST embeds: matched nested under `.import`, created flat.
  //
  // Account-scoped ON PURPOSE — this pair IS the Cleared column's predicate,
  // which has to equal the clearing bridge's. It is empty on every non-feed
  // account's register; the import badges read `org_*_imports` below.
  matched_imports: Array<{ import: { id: string; booking_date: string; description: string | null } }>;
  created_imports: Array<{ id: string; booking_date: string; description: string | null }>;
  // The same links ORG-WIDE, merged in by the register route from the shared
  // IMPORT_MATCH_EMBED. Optional, and ABSENT ≠ EMPTY: `[]` means "no feed link
  // anywhere" and wins over the scoped pair, while absent (a row the
  // enrichment missed, or an API older than these fields) means "no answer"
  // and makes the mapper fall back to the scoped pair.
  org_matched_imports?: Array<{ import: { id: string; booking_date: string; description: string | null } }>;
  org_created_imports?: Array<{ id: string; booking_date: string; description: string | null }>;
  created_by: string;
  created_at: string;
  updated_at: string;
  entry_debit_amount: number;
  entry_credit_amount: number;
  running_balance: number;
  // Void linkage (audit) — the register RPC exposes it via the shared
  // ledger_void_linkage view so a reversed bank payment strikes through + gets
  // a Reversal cross-link, same as the org register. Nullable; the `...t` spread
  // in the register hook carries these straight from the RPC row.
  void_of_transaction_id: string | null;
  reverses_transaction_number: string | null;
  reversed_by_transaction_id: string | null;
  reversed_by_transaction_number: string | null;
  // Additional joined data (fetched separately for display)
  entries?: LedgerEntry[];
  project?: { name: string };
  business?: { name: string };
  person?: { first_name: string; last_name: string };
  fund?: { fund_code: string; fund_name: string };
  ap_invoice?: { id: string; invoice_number: string };
  ar_invoice?: {
    id: string;
    invoice_number: string;
    source?: string | null;
    sales_channel_id?: string | null;
  };
  expense?: {
    id: string;
    expense_number: string;
    custom_business_name?: string | null;
    business?: { name: string } | null;
  }[];
}

/**
 * Hook to fetch transactions for a specific account with running balances
 * Uses an RPC function that calculates running balance using PostgreSQL window functions
 */
/**
 * Hook to fetch entries for a single transaction on demand (lazy loading).
 * Used by expandable row details to avoid loading all entries in the list query.
 */
export const useLedgerTransactionEntries = (transactionId?: string) => {
  return useQuery({
    queryKey: ['ledgerTransactionEntries', transactionId],
    queryFn: async () => {
      if (!transactionId) return [];

      const { data, error } = await supabase
        .from('ledger_entries')
        .select(`
          *,
          account:chart_of_accounts(
            account_name,
            account_code:account_number,
            currency,
            system_account_type,
            category:financial_account_categories(category_name),
            ${ACCOUNT_TYPE_EMBED}
          ),
          fund:funds(fund_code, fund_name),
          tax_rate:tax_rates(name, rate, direction)
        `)
        .eq('transaction_id', transactionId)
        .order('debit_amount', { ascending: false });

      if (error) throw error;
      return (data || []) as LedgerEntry[];
    },
    enabled: !!transactionId,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes — entries rarely change
  });
};

/**
 * Relationship label returned by get_related_ledger_transactions, describing how
 * a related transaction connects to the one being viewed. Strongest label wins
 * when a transaction qualifies under several (see the RPC's DISTINCT ON rank).
 */
export type RelatedTransactionRelationship =
  | 'bill'                // the AP bill posting this payment/void belongs to
  | 'invoice'             // the AR invoice posting this payment belongs to
  | 'payment'             // a payment applied to the same bill/invoice/VAT return
  | 'reversal'            // the void/reversal of this posting — or any void tagged to the same bill/invoice
  | 'original'            // the original posting this void reverses — or a replaced (edited) posting of the same bill/invoice
  | 'bank_import_sibling' // created from / matched to the same bank-statement line
  | 'vat_settlement'      // the VAT settlement posting this payment belongs to
  | 'reference_sibling';  // shares an invoice number / purchase order

export interface RelatedLedgerTransaction {
  related_transaction_id: string;
  transaction_number: string;
  transaction_date: string;
  description: string;
  total_amount: number;
  relationship: RelatedTransactionRelationship;
  relationship_detail: string | null;
}

/**
 * Lazy-loads the transactions related to a given transaction (bill ↔ payments,
 * reversals, bank-import siblings, VAT settlement, shared reference) through
 * the Hono backend (GET .../orgs/:orgId/transactions/:transactionId/related →
 * the `get_related_ledger_transactions` SECURITY INVOKER RPC). Mirrors
 * `useLedgerTransactionEntries`: it fires only on row expand, and a single
 * round-trip avoids per-relationship N+1 queries.
 *
 * `organizationId` is needed only to address the route — it is NOT part of the
 * query key, deliberately. `transactionId` is a globally-unique UUID, so the
 * `['relatedLedgerTransactions', transactionId]` key cannot bleed across orgs;
 * the key is the data contract and stays exactly as it was. Authorization is
 * unchanged either way: the route's `requireOrgMember()` plus the RPC's RLS.
 */
export const useRelatedLedgerTransactions = (
  transactionId?: string,
  organizationId?: string,
) => {
  return useQuery({
    queryKey: ['relatedLedgerTransactions', transactionId],
    queryFn: async (): Promise<RelatedLedgerTransaction[]> => {
      if (!transactionId || !organizationId) return [];

      const { related } = await authedGet<RelatedLedgerTransactionsResponse>(
        `/api/v1/ledger-transactions/orgs/${organizationId}/transactions/${transactionId}/related`,
      );
      return related;
    },
    enabled: !!transactionId && !!organizationId,
    staleTime: 5 * 60 * 1000, // Related links rarely change — cache for 5 minutes
  });
};

export const useAccountTransactionsWithBalance = (
  organizationId?: string,
  accountId?: string
) => {
  return useQuery({
    queryKey: ['accountTransactionsWithBalance', organizationId, accountId],
    queryFn: async (): Promise<TransactionWithRunningBalance[]> => {
      if (!organizationId || !accountId) return [];

      // ONE round trip to the Hono backend (GET .../accounts/:accountId/register).
      // The route runs the SECURITY INVOKER running-balance RPC on the
      // per-request user client (org RLS authorizes) and merges the enrichment
      // embeds — entries, project, business, person, fund, ap/ar invoice,
      // expense — in by transaction id server-side.
      //
      // That enrichment used to run HERE, as a second query with
      // `.in('id', everyTransactionId)`. Two defects, both now structurally
      // out of reach because the route chunks the ids (≤100 per `.in()`, the
      // register-calibrated REGISTER_ENRICHMENT_CHUNK_SIZE):
      // the URL exceeded Kong's limit on a busy account (~344 journals ≈ 39KB
      // — rejected before routing, so the browser saw a CORS failure and the
      // grid rendered empty), and, below that, PostgREST's silent 1,000-row
      // cap dropped the enrichment off every row past the first thousand.
      const { transactions } = await authedGet<AccountRegisterResponse>(
        `/api/v1/accounts/orgs/${organizationId}/accounts/${accountId}/register`,
      );

      return transactions.map((t) => ({
        ...t,
        // `t.transaction_type` on the wire is the DB-stored "transaction
        // kind" (standard / currency_transfer / vat_payment /
        // vat_settlement) tracking which writer created the journal — NOT
        // the income/expense/transfer display category. The route passes it
        // through untouched; always derive the display category from the
        // entries here, otherwise 'standard' would short-circuit the fallback
        // and render every journal as "Transfer".
        // `is_opening_balance` (carried untouched by the `...t` spread) is
        // the escape hatch for the one consumer that DOES need the raw tag —
        // the register's Cleared column.
        transaction_type: determineTransactionType(t.entries),
      }));
    },
    enabled: !!organizationId && !!accountId,
  });
};
